import { PDF_ANSWER_BOX_DEFAULT_FONT_SIZE } from "@/constants/UiConstants"
import { Placement, UserPlacement } from "@/types/questionnaire/models"
import { normalizeOptionTextLower } from "@/utils/questionnaire/optionText"

export type AnswerPlacementPayload = {
  page: number
  x: number
  y: number
  width: number
  height: number
  font_size: number
  text: string
  options_placements?: Array<{
    text: string
    x: number
    y: number
    width: number
    height: number
    page: number
  }> | null
}

export type OptionPlacement = {
  text: string
  x: number
  y: number
  width: number
  height: number
  page: number
}

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

// Backwards-friendly alias used widely by the PDF answering flow.
// It normalizes for case-insensitive comparisons.
export const normalizeOptionText = normalizeOptionTextLower

export const stringifyStringArray = (values: string[]) =>
  JSON.stringify(
    values
      .filter((v) => typeof v === "string")
      .map((v) => v.trim())
      .filter(Boolean),
  )

export const toApiPlacement = (up: UserPlacement): AnswerPlacementPayload => ({
  page: up.page,
  x: up.x,
  y: up.y,
  width: up.width,
  height: up.height,
  font_size: up.fontSize ?? PDF_ANSWER_BOX_DEFAULT_FONT_SIZE,
  text: up.text || "",
  options_placements: up.optionsPlacements ?? null,
})

export const ensureUserPlacement = (
  existing: UserPlacement | null | undefined,
  extractionHint: Placement | null | undefined,
  fallbackText: string,
  patch: Partial<UserPlacement>,
): UserPlacement => {
  if (existing) {
    return {
      ...existing,
      ...patch,
      text: patch.text ?? existing.text,
      isUserPositioned: patch.isUserPositioned ?? existing.isUserPositioned,
    }
  }

  const base: UserPlacement = {
    page: extractionHint?.page || 1,
    x: extractionHint?.x || 0,
    y: extractionHint?.y || 0,
    width: extractionHint?.w || 100,
    height: extractionHint?.h || 30,
    fontSize: extractionHint?.fontSize || PDF_ANSWER_BOX_DEFAULT_FONT_SIZE,
    text: fallbackText || null,
    isUserPositioned: true,
    updatedAt: new Date().toISOString(),
    optionsPlacements: [],
  }

  return {
    ...base,
    ...patch,
    fontSize: patch.fontSize ?? extractionHint?.fontSize ?? base.fontSize,
    text: patch.text ?? base.text,
    isUserPositioned: patch.isUserPositioned ?? base.isUserPositioned,
    updatedAt: patch.updatedAt ?? base.updatedAt,
  }
}
